import React, { useState } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Card,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import DownloadIcon from '@mui/icons-material/Download'
import AddIcon from '@mui/icons-material/Add'

const StyledPaper = styled(Paper)(() => ({
  padding: '24px',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  background: '#fff',
}))

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#f8f9fa',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
}))

const ResultCard = styled(Card)(() => ({
  height: 300,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  },
}))

const ImagesContainer = () => {
  const [formData, setFormData] = useState({
    project: 'Mrx en el cielo',
    prompt: '',
    size: '19:6 1233X23',
    models: ['Ohwx raider 125'],
    steps: 20,
    guidance: 5,
    seed: '34234234',
    seedType: 'Random'
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    })
  }

  const handleGenerate = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Por ahora solo mostramos en consola
      console.log('Generating image with parameters:', formData)

      setSuccess(true)
    } catch (err) {
      setError(err.message || 'Error al generar la imagen')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseSnackbar = () => {
    setError(null)
    setSuccess(false)
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Proyecto</InputLabel>
          <Select
            value={formData.project}
            onChange={handleInputChange('project')}
            label="Proyecto"
            size="small"
          >
            <MenuItem value="Mrx en el cielo">Mrx en el cielo</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Tokens: 3300/5000
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ResultCard>
                <Box sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">
                    Resultado 1
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', gap: 1, bgcolor: '#f8f9fa' }}>
                  <Button
                    startIcon={<DownloadIcon />}
                    variant="text"
                    size="small"
                  >
                    Download
                  </Button>
                </Box>
              </ResultCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ResultCard>
                <Box sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">
                    Resultado 2
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', gap: 1, bgcolor: '#f8f9fa' }}>
                  <Button
                    startIcon={<DownloadIcon />}
                    variant="text"
                    size="small"
                  >
                    Download
                  </Button>
                </Box>
              </ResultCard>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Prompt</Typography>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                placeholder="Describe la imagen que deseas generar..."
                value={formData.prompt}
                onChange={handleInputChange('prompt')}
                variant="outlined"
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tamaño</InputLabel>
                  <Select
                    value={formData.size}
                    onChange={handleInputChange('size')}
                    label="Tamaño"
                  >
                    <MenuItem value="19:6 1233X23">19:6 1233X23</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel>Modelos</InputLabel>
                    <Select
                      value={formData.models[0]}
                      onChange={handleInputChange('models')}
                      label="Modelos"
                    >
                      <MenuItem value="Ohwx raider 125">Ohwx raider 125</MenuItem>
                    </Select>
                  </FormControl>
                  <Button 
                    variant="outlined" 
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Steps"
                  type="number"
                  value={formData.steps}
                  onChange={handleInputChange('steps')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Guidance"
                  type="number"
                  value={formData.guidance}
                  onChange={handleInputChange('guidance')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Seed"
                  value={formData.seed}
                  onChange={handleInputChange('seed')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Seed type</InputLabel>
                  <Select
                    value={formData.seedType}
                    onChange={handleInputChange('seedType')}
                    label="Seed type"
                  >
                    <MenuItem value="Random">Random</MenuItem>
                    <MenuItem value="Increment">Increment</MenuItem>
                    <MenuItem value="Decrement">Decrement</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGenerate}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generar'}
              </Button>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Imagen generada exitosamente
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ImagesContainer 